import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import Typography from '@sharedComponents/Typography';
import { useTranslation } from 'react-i18next';

import OverrideBadge from '../OverrideBadge';
import { overriddenLanguages, targetKey, targetOf } from '../overrideRules';
import TargetLabel from '../TargetLabel';

import * as style from './style.module.css';

const noop = () => {};

const isOverridden = (text) => overriddenLanguages(text).length > 0;

/**
 * Block of the rows of a restricted collection that keep an overridden text without giving access: for each
 * target, its label, the badges of its overridden fields, counted against the catalogue `languages`, a button
 * that authorizes it and a bin that deletes its override. `disabled`, or the `kind:uuid` key of the target in
 * the `pendingKeys` array, disables the buttons of a target. Nothing is shown without a row.
 */
const WithoutAccessList = ({ rows = [], languages = [], disabled = false, pendingKeys = [], onAuthorize = noop, onRemove = noop }) => {
    const { t } = useTranslation('bo');
    if (rows.length === 0) return null;

    const total = languages.length;

    return (
        <FlexBox flexDirection="column" gap="s" className={style.block}>
            <Typography seo={4} fontWeight="bold" color="neutral-primary">
                {t('Overrides kept without access ({{total}})', { total: rows.length })}
            </Typography>
            <Typography fontSize="s" color="neutral-secondary">
                {t(
                    'These clients and platforms keep an overridden title or description, but do not see the collection: their own row prevails, even if their client is authorized. Authorize them or delete their override.',
                )}
            </Typography>
            <FlexBox role="list" flexDirection="column" gap="xs">
                {rows.map((row) => {
                    const target = targetOf(row);
                    const locked = disabled || pendingKeys.includes(targetKey(target));
                    const removeLabel = t('Delete the override of {{name}}', { name: target.name });
                    return (
                        <FlexBox key={row.uuid} role="listitem" alignItems="center" gap="s" flexWrap="wrap" className={style.item}>
                            <FlexBox flexGrow={1} flexShrink={1} flexBasis="240px">
                                <TargetLabel row={row} />
                            </FlexBox>
                            <FlexBox alignItems="center" gap="s" flexWrap="wrap">
                                {isOverridden(row.title) && <OverrideBadge text={row.title} total={total} field="title" />}
                                {isOverridden(row.description) && (
                                    <OverrideBadge text={row.description} total={total} field="description" />
                                )}
                                <Button
                                    outline
                                    size="xs"
                                    label={t('Authorize')}
                                    aria-label={t('Authorize {{name}}', { name: target.name })}
                                    disabled={locked}
                                    action={() => onAuthorize(row)}
                                />
                                <Button
                                    ghost
                                    icon={faTrash}
                                    title={removeLabel}
                                    aria-label={removeLabel}
                                    disabled={locked}
                                    action={() => onRemove(row)}
                                />
                            </FlexBox>
                        </FlexBox>
                    );
                })}
            </FlexBox>
        </FlexBox>
    );
};

export default WithoutAccessList;
